// src/stations/scanner/uploader.ts — Schoolwork File & Text Uploader (T-6.1.1, FR-SCAN-001)
// Supports multi-modal input:
// 1. Image upload (PNG/JPEG/WEBP, <= 10MB) with drag-and-drop
// 2. Camera capture (<input type="file" capture="environment">) for Samsung Tab A9+
// 3. Raw text input alternative for typed worksheets/questions
// 4. Quick sample worksheet presets for instant demoing

import { tokens } from '@ui/designTokens';
import { soundEngine } from '@audio/soundEngine';

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB (FR-SCAN-001)
export const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

export interface UploadPayload {
  sourceType: 'image_upload' | 'camera_capture' | 'text_entry';
  title: string;
  fileName?: string;
  fileSizeBytes?: number;
  imageDataUrl?: string;
  rawTextContent?: string;
}

export interface UploaderOptions {
  onUploadSuccess: (payload: UploadPayload) => void;
  onUploadError?: (errorMsg: string) => void;
}

export class SchoolworkUploader {
  private container: HTMLElement;
  private onUploadSuccess: (payload: UploadPayload) => void;
  private onUploadError?: (errorMsg: string) => void;
  private currentMode: 'file' | 'text' = 'file';

  constructor(options: UploaderOptions) {
    this.container = document.createElement('div');
    this.container.className = 'schoolwork-uploader-container';
    this.onUploadSuccess = options.onUploadSuccess;
    this.onUploadError = options.onUploadError;
    this.render();
  }

  getElement(): HTMLElement {
    return this.container;
  }

  static validateFile(file: File): { valid: boolean; error?: string } {
    if (!file) {
      return { valid: false, error: 'No file selected.' };
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type.toLowerCase())) {
      return {
        valid: false,
        error: `Unsupported file format (${file.type || 'unknown'}). Please upload a PNG, JPEG, or WEBP image.`,
      };
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      return {
        valid: false,
        error: `File is too large (${mb} MB). Maximum allowed size is 10 MB.`,
      };
    }
    return { valid: true };
  }

  render(): HTMLElement {
    this.container.innerHTML = `
      <section class="morris-card" style="padding: 28px; max-width: 800px; margin: 0 auto; box-shadow: 0 6px 28px rgba(7, 20, 36, 0.1);">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 14px; margin-bottom: 20px;">
          <div>
            <div style="font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">
              📷 KS2 Schoolwork Analyzer & Remediation Drill
            </div>
            <h2 style="font-family: ${tokens.font.heading}; font-size: 2rem; color: ${tokens.color.morrisIndigo}; margin: 4px 0 8px;">
              Upload Tudor's Schoolwork
            </h2>
            <p style="font-size: 1rem; color: #3A4D66; margin: 0; max-width: 540px; line-height: 1.5;">
              Snap a photo of Tudor's school notebook, drop an image, or paste question text. Clawey identifies learning gaps and generates 5 adaptive marine drill questions.
            </p>
          </div>
          <span style="font-size: 3rem;">🔬</span>
        </div>

        <!-- Mode Switcher Tabs -->
        <div style="display: flex; gap: 8px; margin-bottom: 22px; border-bottom: 1.5px solid rgba(26, 54, 93, 0.15); padding-bottom: 12px;">
          <button
            id="tab-mode-file"
            type="button"
            class="touch-target"
            style="
              padding: 10px 20px;
              font-weight: 800;
              font-size: 0.95rem;
              border-radius: 12px;
              border: 1.5px solid ${this.currentMode === 'file' ? tokens.color.morrisIndigo : 'rgba(26, 54, 93, 0.2)'};
              background: ${this.currentMode === 'file' ? 'rgba(255, 246, 229, 0.95)' : '#FFFDF9'};
              color: ${tokens.color.morrisIndigo};
              cursor: pointer;
            "
          >
            📸 Photo / Image File
          </button>

          <button
            id="tab-mode-text"
            type="button"
            class="touch-target"
            style="
              padding: 10px 20px;
              font-weight: 800;
              font-size: 0.95rem;
              border-radius: 12px;
              border: 1.5px solid ${this.currentMode === 'text' ? tokens.color.morrisIndigo : 'rgba(26, 54, 93, 0.2)'};
              background: ${this.currentMode === 'text' ? 'rgba(255, 246, 229, 0.95)' : '#FFFDF9'};
              color: ${tokens.color.morrisIndigo};
              cursor: pointer;
            "
          >
            ✏️ Direct Text Entry
          </button>
        </div>

        <!-- Mode A: File / Camera Upload Area -->
        <div id="uploader-file-area" style="display: ${this.currentMode === 'file' ? 'block' : 'none'};">
          <!-- Drag & Drop Zone -->
          <div
            id="scanner-dropzone"
            style="
              border: 2px dashed ${tokens.color.gildedBrass};
              border-radius: 16px;
              padding: 36px 24px;
              text-align: center;
              background: rgba(255, 253, 249, 0.9);
              transition: all 0.2s ease;
              cursor: pointer;
              margin-bottom: 20px;
            "
          >
            <div style="font-size: 3rem; margin-bottom: 8px;">📑</div>
            <h3 style="margin: 0 0 6px; color: ${tokens.color.morrisIndigo}; font-size: 1.3rem;">
              Drop school worksheet photo here
            </h3>
            <p style="margin: 0 0 16px; font-size: 0.95rem; color: #5A6D86;">
              Supports PNG, JPEG, WEBP (Max 10 MB)
            </p>

            <div style="display: flex; justify-content: center; gap: 12px; flex-wrap: wrap;">
              <!-- File picker -->
              <label
                for="file-input-desktop"
                class="btn-primary touch-target"
                style="padding: 10px 22px; font-size: 0.95rem; cursor: pointer; display: inline-flex; align-items: center; gap: 8px;"
              >
                📁 Browse Device Files
              </label>
              <input
                id="file-input-desktop"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style="display: none;"
              />

              <!-- Camera capture button -->
              <label
                for="file-input-camera"
                class="touch-target"
                style="
                  padding: 10px 22px;
                  font-size: 0.95rem;
                  font-weight: 800;
                  color: ${tokens.color.morrisIndigo};
                  border: 1.5px solid ${tokens.color.morrisIndigo};
                  border-radius: 12px;
                  background: #FFFDF9;
                  cursor: pointer;
                  display: inline-flex;
                  align-items: center;
                  gap: 8px;
                "
              >
                📸 Take Live Photo
              </label>
              <input
                id="file-input-camera"
                type="file"
                accept="image/*"
                capture="environment"
                style="display: none;"
              />
            </div>
          </div>
        </div>

        <!-- Mode B: Direct Text Entry Area -->
        <div id="uploader-text-area" style="display: ${this.currentMode === 'text' ? 'block' : 'none'};">
          <div style="margin-bottom: 16px;">
            <label for="text-entry-title" style="display: block; font-weight: 700; color: ${tokens.color.morrisIndigo}; margin-bottom: 6px;">
              Worksheet Title or Subject:
            </label>
            <input
              id="text-entry-title"
              type="text"
              class="touch-target"
              placeholder="e.g., Year 4 Maths: Adding Fractions with Different Denominators"
              style="width: 100%; box-sizing: border-box; padding: 12px 16px; font-size: 1rem; border: 1.5px solid rgba(26,54,93,0.3); border-radius: 12px; background: #FFFDF9;"
            />
          </div>

          <div style="margin-bottom: 20px;">
            <label for="text-entry-body" style="display: block; font-weight: 700; color: ${tokens.color.morrisIndigo}; margin-bottom: 6px;">
              Questions, Tudor's Answers & Teacher Feedback:
            </label>
            <textarea
              id="text-entry-body"
              rows="6"
              class="touch-target"
              placeholder="Paste or type questions here... e.g.:&#10;Q1. 1/3 + 1/2 = 2/5 (Marked incorrect by teacher)&#10;Q2. Measured angle ABC as 130° instead of 50°"
              style="width: 100%; box-sizing: border-box; padding: 12px 16px; font-size: 1rem; line-height: 1.5; border: 1.5px solid rgba(26,54,93,0.3); border-radius: 12px; background: #FFFDF9; font-family: inherit;"
            ></textarea>
          </div>

          <button
            id="btn-submit-text"
            type="button"
            class="btn-primary touch-target"
            style="padding: 12px 28px; font-size: 1rem; width: 100%;"
          >
            🔍 Analyze Text & Diagnose Misconceptions &rarr;
          </button>
        </div>

        <!-- Sample Presets for Instant One-Tap Testing -->
        <div style="margin-top: 24px; padding-top: 20px; border-top: 1.5px solid rgba(26, 54, 93, 0.12);">
          <div style="font-size: 0.85rem; font-weight: 800; color: ${tokens.color.morrisIndigo}; margin-bottom: 10px;">
            ⚡ Quick Sample Worksheets (Tap to load instantly):
          </div>
          <div style="display: flex; gap: 10px; flex-wrap: wrap;">
            <button type="button" class="preset-btn touch-target" data-preset="fractions" style="padding: 8px 14px; font-size: 0.85rem; border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 10px; background: #FFFDF9; color: ${tokens.color.morrisIndigo}; cursor: pointer;">
              🍰 Fraction Denominator Error (4F2)
            </button>
            <button type="button" class="preset-btn touch-target" data-preset="angles" style="padding: 8px 14px; font-size: 0.85rem; border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 10px; background: #FFFDF9; color: ${tokens.color.morrisIndigo}; cursor: pointer;">
              📐 Protractor Scale Reversal (5G4)
            </button>
            <button type="button" class="preset-btn touch-target" data-preset="spag" style="padding: 8px 14px; font-size: 0.85rem; border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 10px; background: #FFFDF9; color: ${tokens.color.morrisIndigo}; cursor: pointer;">
              🐚 Homophones & Fronted Adverbials (4E1)
            </button>
          </div>
        </div>

        <!-- Error Notification Area -->
        <div id="uploader-error-msg" style="display: none; margin-top: 16px; padding: 12px 16px; border-radius: 10px; background: rgba(255, 107, 74, 0.15); color: ${tokens.color.madderCoral}; font-weight: 700; border: 1px solid ${tokens.color.madderCoral};"></div>
      </section>
    `;

    this.attachEventListeners();
    return this.container;
  }

  private attachEventListeners(): void {
    // Mode Switchers
    const tabFile = this.container.querySelector<HTMLButtonElement>('#tab-mode-file');
    const tabText = this.container.querySelector<HTMLButtonElement>('#tab-mode-text');
    tabFile?.addEventListener('click', () => {
      this.currentMode = 'file';
      this.render();
    });
    tabText?.addEventListener('click', () => {
      this.currentMode = 'text';
      this.render();
    });

    // File inputs
    const desktopInput = this.container.querySelector<HTMLInputElement>('#file-input-desktop');
    const cameraInput = this.container.querySelector<HTMLInputElement>('#file-input-camera');

    desktopInput?.addEventListener('change', (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files[0]) {
        this.processFile(files[0], 'image_upload');
      }
    });

    cameraInput?.addEventListener('change', (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files[0]) {
        this.processFile(files[0], 'camera_capture');
      }
    });

    // Drag and Drop
    const dropzone = this.container.querySelector<HTMLElement>('#scanner-dropzone');
    dropzone?.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropzone.style.borderColor = tokens.color.morrisIndigo;
      dropzone.style.background = 'rgba(255, 246, 229, 0.95)';
    });

    dropzone?.addEventListener('dragleave', (e) => {
      e.preventDefault();
      dropzone.style.borderColor = tokens.color.gildedBrass;
      dropzone.style.background = 'rgba(255, 253, 249, 0.9)';
    });

    dropzone?.addEventListener('drop', (e) => {
      e.preventDefault();
      dropzone.style.borderColor = tokens.color.gildedBrass;
      dropzone.style.background = 'rgba(255, 253, 249, 0.9)';
      const files = e.dataTransfer?.files;
      if (files && files[0]) {
        this.processFile(files[0], 'image_upload');
      }
    });

    // Submit Text button
    const submitTextBtn = this.container.querySelector<HTMLButtonElement>('#btn-submit-text');
    submitTextBtn?.addEventListener('click', () => {
      const titleInput = this.container.querySelector<HTMLInputElement>('#text-entry-title');
      const bodyInput = this.container.querySelector<HTMLTextAreaElement>('#text-entry-body');

      const title = titleInput?.value.trim() || 'Custom Schoolwork Note';
      const body = bodyInput?.value.trim() || '';

      if (!body) {
        this.showError('Please enter some text from Tudor’s worksheet before analyzing.');
        return;
      }

      soundEngine.playTap();
      this.onUploadSuccess({
        sourceType: 'text_entry',
        title,
        rawTextContent: body,
      });
    });

    // Sample Presets
    const presetBtns = this.container.querySelectorAll<HTMLButtonElement>('.preset-btn');
    presetBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const type = btn.getAttribute('data-preset');
        soundEngine.playTap();
        this.loadPreset(type);
      });
    });
  }

  private processFile(file: File, sourceType: 'image_upload' | 'camera_capture'): void {
    const validation = SchoolworkUploader.validateFile(file);
    if (!validation.valid) {
      this.showError(validation.error || 'Invalid file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      soundEngine.playTap();
      const payload: UploadPayload = {
        sourceType,
        title: file.name.replace(/\.[^/.]+$/, ''),
        fileName: file.name,
        fileSizeBytes: file.size,
        imageDataUrl: reader.result as string,
      };
      this.onUploadSuccess(payload);
    };

    reader.onerror = () => {
      this.showError('Failed to read the selected file. Please try again.');
    };

    reader.readAsDataURL(file);
  }

  private loadPreset(presetType: string | null): void {
    if (presetType === 'fractions') {
      this.onUploadSuccess({
        sourceType: 'text_entry',
        title: 'Year 4 Fractions: Adding with Different Denominators',
        rawTextContent:
          'Question 1: 1/3 + 1/2 = 2/5 (Tudor added the numerators 1+1=2 and denominators 3+2=5 directly instead of finding the lowest common denominator 6).\nQuestion 2: 2/4 + 1/4 = 3/8 (Tudor added denominators).',
      });
    } else if (presetType === 'angles') {
      this.onUploadSuccess({
        sourceType: 'text_entry',
        title: 'Year 5 Geometry: Measuring Angles with Protractor',
        rawTextContent:
          'Question 1: Angle ABC is an acute angle measured at 130° (Tudor read the outer obtuse scale instead of inner acute scale 50°).\nQuestion 2: Reflex angle estimated at 90° (Misconception: acute vs reflex angle).',
      });
    } else if (presetType === 'spag') {
      this.onUploadSuccess({
        sourceType: 'text_entry',
        title: 'Year 4 SPAG: Homophones & Fronted Adverbials',
        rawTextContent:
          'Sentence 1: "Quickly Tudor swam to they\'re submarine." (Misconception: confusing they\'re, their, and there).\nSentence 2: "Deep beneath the waves Tudor saw an ammonite." (Missing comma after fronted adverbial).',
      });
    }
  }

  private showError(msg: string): void {
    const errEl = this.container.querySelector<HTMLElement>('#uploader-error-msg');
    if (errEl) {
      errEl.textContent = `⚠️ ${msg}`;
      errEl.style.display = 'block';
    }
    if (this.onUploadError) {
      this.onUploadError(msg);
    }
  }
}
